import { doc, setDoc, getDoc, updateDoc, arrayUnion, increment, Timestamp, runTransaction } from 'firebase/firestore';
import { db, auth } from './firebase';
import { updateLeaderboards } from './leaderboardService';

// ==================== INTERFACES ====================

export interface UserAnswer {
  questionId: string | number;
  isCorrect: boolean;
  answeredAt: Timestamp;
  selectedOptionId: string;
  timeSpentSeconds?: number;
  subject?: string;
  topic?: string;
  difficulty?: string;
  examId?: string;
}

export interface UserSubscription {
  isPro: boolean;
  freeQuestionsUsed: number;
  freeQuestionsLimit: number;
  subscribedAt?: Timestamp;
  expiresAt?: Timestamp;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt: Timestamp;
  category: 'streak' | 'accuracy' | 'volume' | 'special' | 'time';
}

export interface UserProgress {
  totalAnswered: number;
  totalCorrect: number;
  history: UserAnswer[];
  isPro: boolean;
  freeQuestionsUsed: number;
  freeQuestionsLimit: number;
  createdAt: Timestamp;
  lastActivityDate?: string;
  currentStreak?: number;
  longestStreak?: number;
  activityHistory?: { [date: string]: number };
  badges?: Badge[];
  weeklyGoal?: number;
  weeklyProgress?: number;
  bookmarks?: string[];
  purchasedItems?: string[];
  purchases?: Record<string, any>;
  nickname?: string; // Adicionado para o perfil do usuário
  targetExam?: string; // Adicionado para o perfil do usuário
  photoURL?: string; // Adicionado para o perfil do usuário
}

// ==================== CONSTANTS ====================

const FREE_QUESTIONS_LIMIT = 10;
const APP_TIMEZONE = 'America/Sao_Paulo';

const toDateKey = (date: Date = new Date(), timeZone: string = APP_TIMEZONE): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

const toDateKeyDaysFrom = (baseDate: Date, dayDelta: number, timeZone: string = APP_TIMEZONE): string => {
  const d = new Date(baseDate);
  d.setDate(d.getDate() + dayDelta);
  return toDateKey(d, timeZone);
};

const getRollingWeeklyProgress = (activityHistory: { [date: string]: number } = {}, refDate: Date = new Date()): number => {
  let total = 0;
  for (let i = 0; i < 7; i++) {
    const dayKey = toDateKeyDaysFrom(refDate, -i);
    total += Number(activityHistory[dayKey] || 0);
  }
  return total;
};

// DefiniÃ§Ã£o de todos os badges disponÃ­veis
const BADGE_DEFINITIONS = {
  // Badges de Volume
  first_step: { name: 'Primeiro Passo', description: 'Respondeu sua primeira questÃ£o', icon: 'ðŸ‘¶', category: 'volume' as const, requirement: 1 },
  ten_questions: { name: 'Esquentando', description: 'Respondeu 10 questÃµes', icon: 'ðŸ”Ÿ', category: 'volume' as const, requirement: 10 },
  fifty_questions: { name: 'Dedicado', description: 'Respondeu 50 questÃµes', icon: 'ðŸ“š', category: 'volume' as const, requirement: 50 },
  hundred_questions: { name: 'Especialista', description: 'Respondeu 100 questÃµes', icon: 'ðŸŽ“', category: 'volume' as const, requirement: 100 },
  five_hundred: { name: 'Veterano', description: 'Respondeu 500 questÃµes', icon: 'ðŸ…', category: 'volume' as const, requirement: 500 },
  thousand: { name: 'Mestre', description: 'Respondeu 1000 questÃµes', icon: 'ðŸ‘‘', category: 'volume' as const, requirement: 1000 },

  // Badges de Streak
  three_day_streak: { name: 'ComeÃ§ando Bem', description: '3 dias seguidos estudando', icon: 'ðŸ”¥', category: 'streak' as const, requirement: 3 },
  seven_day_streak: { name: 'Semana Perfeita', description: '7 dias seguidos estudando', icon: 'ðŸ“…', category: 'streak' as const, requirement: 7 },
  fourteen_day_streak: { name: 'InabalÃ¡vel', description: '14 dias seguidos estudando', icon: 'ðŸ’ª', category: 'streak' as const, requirement: 14 },
  thirty_day_streak: { name: 'MÃªs de Ouro', description: '30 dias seguidos estudando', icon: 'ðŸŒŸ', category: 'streak' as const, requirement: 30 },
  sixty_day_streak: { name: 'ImparÃ¡vel', description: '60 dias seguidos estudando', icon: 'ðŸš€', category: 'streak' as const, requirement: 60 },
  hundred_day_streak: { name: 'LendÃ¡rio', description: '100 dias seguidos estudando', icon: 'ðŸ†', category: 'streak' as const, requirement: 100 },

  // Badges de PrecisÃ£o
  first_correct: { name: 'Primeira VitÃ³ria', description: 'Acertou sua primeira questÃ£o', icon: 'âœ…', category: 'accuracy' as const, requirement: 1 },
  ten_correct: { name: 'Boa Mira', description: 'Acertou 10 questÃµes', icon: 'ðŸŽ¯', category: 'accuracy' as const, requirement: 10 },
  fifty_correct: { name: 'PrecisÃ£o', description: 'Acertou 50 questÃµes', icon: 'ðŸ’Ž', category: 'accuracy' as const, requirement: 50 },
  hundred_correct: { name: 'Atirador de Elite', description: 'Acertou 100 questÃµes', icon: 'ðŸ¹', category: 'accuracy' as const, requirement: 100 },
  champion: { name: 'CampeÃ£o', description: 'MantÃ©m 80%+ de acerto (mÃ­n. 20 questÃµes)', icon: 'ðŸ†', category: 'accuracy' as const, requirement: 80 },
  perfectionist: { name: 'Perfeccionista', description: 'MantÃ©m 90%+ de acerto (mÃ­n. 50 questÃµes)', icon: 'ðŸ’¯', category: 'accuracy' as const, requirement: 90 },

  // Badges de Tempo
  early_bird: { name: 'Madrugador', description: 'Estudou antes das 7h', icon: 'ðŸŒ…', category: 'time' as const, requirement: 7 },
  night_owl: { name: 'Coruja', description: 'Estudou apÃ³s as 23h', icon: 'ðŸ¦‰', category: 'time' as const, requirement: 23 },
  weekend_warrior: { name: 'Guerreiro de Fim de Semana', description: 'Estudou no sÃ¡bado e domingo', icon: 'âš”ï¸', category: 'time' as const, requirement: 0 },

  // Badges Especiais
  perfect_day: { name: 'Dia Perfeito', description: '10+ questÃµes com 100% de acerto em um dia', icon: 'â­', category: 'special' as const, requirement: 10 },
  comeback: { name: 'Volta por Cima', description: 'Voltou apÃ³s 7+ dias sem estudar', icon: 'ðŸ”„', category: 'special' as const, requirement: 7 },
  marathon: { name: 'Maratonista', description: '50+ questÃµes em um Ãºnico dia', icon: 'ðŸƒ', category: 'special' as const, requirement: 50 },
  consistent: { name: 'Consistente', description: 'Estudou em todos os dias da semana', icon: 'ðŸ“Š', category: 'special' as const, requirement: 7 }
};

// ==================== BADGE CALCULATION ====================

const calculateVolumeBadges = (totalAnswered: number, existingBadgeIds: string[]): Badge[] => {
  const newBadges: Badge[] = [];

  const addBadge = (id: string) => {
    if (!existingBadgeIds.includes(id) && BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS]) {
      const def = BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS];
      newBadges.push({
        id,
        name: def.name,
        description: def.description,
        icon: def.icon,
        category: def.category,
        unlockedAt: Timestamp.now()
      });
    }
  };

  if (totalAnswered >= 1) addBadge('first_step');
  if (totalAnswered >= 10) addBadge('ten_questions');
  if (totalAnswered >= 50) addBadge('fifty_questions');
  if (totalAnswered >= 100) addBadge('hundred_questions');
  if (totalAnswered >= 500) addBadge('five_hundred');
  if (totalAnswered >= 1000) addBadge('thousand');

  return newBadges;
};

const calculateStreakBadges = (currentStreak: number, existingBadgeIds: string[]): Badge[] => {
  const newBadges: Badge[] = [];

  const addBadge = (id: string) => {
    if (!existingBadgeIds.includes(id) && BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS]) {
      const def = BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS];
      newBadges.push({
        id,
        name: def.name,
        description: def.description,
        icon: def.icon,
        category: def.category,
        unlockedAt: Timestamp.now()
      });
    }
  };

  if (currentStreak >= 3) addBadge('three_day_streak');
  if (currentStreak >= 7) addBadge('seven_day_streak');
  if (currentStreak >= 14) addBadge('fourteen_day_streak');
  if (currentStreak >= 30) addBadge('thirty_day_streak');
  if (currentStreak >= 60) addBadge('sixty_day_streak');
  if (currentStreak >= 100) addBadge('hundred_day_streak');

  return newBadges;
};

const calculateAccuracyBadges = (totalAnswered: number, totalCorrect: number, isCorrect: boolean, existingBadgeIds: string[]): Badge[] => {
  const newBadges: Badge[] = [];

  const addBadge = (id: string) => {
    if (!existingBadgeIds.includes(id) && BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS]) {
      const def = BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS];
      newBadges.push({
        id,
        name: def.name,
        description: def.description,
        icon: def.icon,
        category: def.category,
        unlockedAt: Timestamp.now()
      });
    }
  };

  if (isCorrect && totalCorrect >= 1) addBadge('first_correct');
  if (totalCorrect >= 10) addBadge('ten_correct');
  if (totalCorrect >= 50) addBadge('fifty_correct');
  if (totalCorrect >= 100) addBadge('hundred_correct');

  // Badge CampeÃ£o: 80%+ de acerto com mÃ­nimo de 20 questÃµes
  if (totalAnswered >= 20) {
    const accuracy = (totalCorrect / totalAnswered) * 100;
    if (accuracy >= 80) addBadge('champion');
  }

  // Badge Perfeccionista: 90%+ de acerto com mÃ­nimo de 50 questÃµes
  if (totalAnswered >= 50) {
    const accuracy = (totalCorrect / totalAnswered) * 100;
    if (accuracy >= 90) addBadge('perfectionist');
  }

  return newBadges;
};

const calculateTimeBadges = (hour: number, dayOfWeek: number, activityHistory: { [date: string]: number }, now: Date, existingBadgeIds: string[]): Badge[] => {
  const newBadges: Badge[] = [];

  const addBadge = (id: string) => {
    if (!existingBadgeIds.includes(id) && BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS]) {
      const def = BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS];
      newBadges.push({
        id,
        name: def.name,
        description: def.description,
        icon: def.icon,
        category: def.category,
        unlockedAt: Timestamp.now()
      });
    }
  };

  // Madrugador: antes das 7h
  if (hour < 7) addBadge('early_bird');

  // Coruja: apÃ³s as 23h
  if (hour >= 23) addBadge('night_owl');

  // Guerreiro de Fim de Semana: estudou sÃ¡bado E domingo
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    const saturday = new Date(now);
    const sunday = new Date(now);
    
    if (dayOfWeek === 0) { // Domingo
      saturday.setDate(saturday.getDate() - 1);
    } else { // SÃ¡bado
      sunday.setDate(sunday.getDate() + 1);
    }
    
    const saturdayStr = toDateKey(saturday);
    const sundayStr = toDateKey(sunday);
    
    if (activityHistory[saturdayStr] && activityHistory[sundayStr]) {
      addBadge('weekend_warrior');
    }
  }

  return newBadges;
};

const calculateSpecialBadges = (history: UserAnswer[], isCorrect: boolean, answeredAt: Timestamp, activityHistory: { [date: string]: number }, data: any, today: string, existingBadgeIds: string[]): Badge[] => {
  const newBadges: Badge[] = [];
  const now = new Date(); // Definindo a variÃ¡vel 'now' aqui

  const addBadge = (id: string) => {
    if (!existingBadgeIds.includes(id) && BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS]) {
      const def = BADGE_DEFINITIONS[id as keyof typeof BADGE_DEFINITIONS];
      newBadges.push({
        id,
        name: def.name,
        description: def.description,
        icon: def.icon,
        category: def.category,
        unlockedAt: Timestamp.now()
      });
    }
  };

  // Dia Perfeito: 10+ questÃµes com 100% de acerto em um dia
  const todayAnswers = [...history, { questionId: '', isCorrect, answeredAt, selectedOptionId: '' }]
    .filter(a => {
      const answerDate = toDateKey(new Date(a.answeredAt.toMillis()));
      return answerDate === today;
    });
  
  if (todayAnswers.length >= 10) {
    const allCorrect = todayAnswers.every(a => a.isCorrect);
    if (allCorrect) addBadge('perfect_day');
  }

  // Maratonista: 50+ questÃµes em um dia
  const todayCount = (activityHistory[today] || 0) + 1;
  if (todayCount >= 50) addBadge('marathon');

  // Volta por Cima: voltou apÃ³s 7+ dias sem estudar
  const lastActivityDate = data?.lastActivityDate;
  if (lastActivityDate && lastActivityDate !== today) {
    const lastDate = new Date(lastActivityDate + 'T00:00:00');
    const diffDays = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays >= 7) addBadge('comeback');
  }

  // Consistente: estudou em todos os 7 dias da semana
  const weekDays: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    weekDays.push(toDateKey(d));
  }
  const studiedAllWeek = weekDays.every(day => activityHistory[day] && activityHistory[day] > 0);
  if (studiedAllWeek) addBadge('consistent');

  return newBadges;
};

const calculateBadges = async (userId: string, currentProgress: UserProgress, latestAnswer: UserAnswer): Promise<Badge[]> => {
  const existingBadges: Badge[] = currentProgress?.badges || [];
  const existingBadgeIds = existingBadges.map(b => b.id);
  const newBadges: Badge[] = [];

  // Os totais jÃ¡ devem incluir a resposta atual para o cÃ¡lculo dos badges
  const totalAnswered = currentProgress.totalAnswered;
  const totalCorrect = currentProgress.totalCorrect;
  const currentStreak = currentProgress.currentStreak || 0;
  const activityHistory = currentProgress.activityHistory || {};
  const history: UserAnswer[] = currentProgress.history || [];

  const now = new Date(latestAnswer.answeredAt.toMillis());
  const today = toDateKey(now);
  const hour = now.getHours();
  const dayOfWeek = now.getDay();

  newBadges.push(...calculateVolumeBadges(totalAnswered, existingBadgeIds));
  newBadges.push(...calculateStreakBadges(currentStreak, existingBadgeIds));
  newBadges.push(...calculateAccuracyBadges(totalAnswered, totalCorrect, latestAnswer.isCorrect, existingBadgeIds));
  newBadges.push(...calculateTimeBadges(hour, dayOfWeek, activityHistory, now, existingBadgeIds));
  // Para badges especiais, passamos a histÃ³ria completa, incluindo a nova resposta
  newBadges.push(...calculateSpecialBadges([...history, latestAnswer], latestAnswer.isCorrect, latestAnswer.answeredAt, activityHistory, currentProgress, today, existingBadgeIds));

  return newBadges;
};

// ==================== STREAK CALCULATION ====================

const calculateStreak = (lastActivityDate: string | undefined, currentStreak: number, today: string): number => {
  if (!lastActivityDate) return 1;
  if (lastActivityDate === today) return currentStreak;

  const yesterdayStr = toDateKeyDaysFrom(new Date(), -1);

  if (lastActivityDate === yesterdayStr) {
    return currentStreak + 1;
  }

  return 1; // Quebrou a sequÃªncia
};

// ==================== MAIN FUNCTIONS ====================

export const saveUserAnswer = async (questionId: string | number, selectedOptionId: string, isCorrect: boolean, timeSpentSeconds: number, topic: string, difficulty: string, examId: string, subject?: string): Promise<string[]> => {
  const user = auth.currentUser;
  if (!user) return [];

  const userRef = doc(db, 'userProgress', user.uid);
  const answer: UserAnswer = {
    questionId,
    selectedOptionId,
    isCorrect,
    answeredAt: Timestamp.now(),
    timeSpentSeconds,
    subject,
    topic,
    difficulty,
    examId,
  };

  try {
    const now = new Date();
    const today = toDateKey(now);
    let newlyUnlockedBadges: Badge[] = [];
    let isProUser = false;
    let nickname = user.email?.split('@')[0] || 'Concurseiro';
    let photoURL = user.photoURL || '';

    await runTransaction(db, async (tx) => {
      const userDoc = await tx.get(userRef);

      let currentProgress: UserProgress;
      if (!userDoc.exists()) {
        currentProgress = {
          totalAnswered: 0,
          totalCorrect: 0,
          history: [],
          isPro: false,
          freeQuestionsUsed: 0,
          freeQuestionsLimit: FREE_QUESTIONS_LIMIT,
          createdAt: Timestamp.now(),
          lastActivityDate: '',
          currentStreak: 0,
          longestStreak: 0,
          activityHistory: {},
          badges: [],
          weeklyGoal: 50,
          weeklyProgress: 0,
          purchasedItems: [],
          purchases: {},
        };
      } else {
        currentProgress = userDoc.data() as UserProgress;
      }

      nickname = currentProgress.nickname || user.displayName?.split(' ')[0] || user.email?.split('@')[0] || nickname;
      photoURL = currentProgress.photoURL || user.photoURL || '';
      isProUser = Boolean(currentProgress.isPro);

      let freeQuestionsUsed = currentProgress.freeQuestionsUsed || 0;
      if (currentProgress.lastActivityDate && currentProgress.lastActivityDate !== today) {
        freeQuestionsUsed = 0;
      }

      const simulatedTotalAnswered = (currentProgress.totalAnswered || 0) + 1;
      const simulatedTotalCorrect = (currentProgress.totalCorrect || 0) + (isCorrect ? 1 : 0);

      const newStreak = calculateStreak(currentProgress.lastActivityDate, currentProgress.currentStreak || 0, today);
      const longestStreak = Math.max(newStreak, currentProgress.longestStreak || 0);
      const updatedActivityHistory = {
        ...(currentProgress.activityHistory || {}),
        [today]: (((currentProgress.activityHistory || {})[today] || 0) + 1),
      };
      const weeklyProgress = getRollingWeeklyProgress(updatedActivityHistory, now);

      const progressForBadgeCalc: UserProgress = {
        ...currentProgress,
        totalAnswered: simulatedTotalAnswered,
        totalCorrect: simulatedTotalCorrect,
        history: [...(currentProgress.history || []), answer],
        lastActivityDate: today,
        currentStreak: newStreak,
        longestStreak,
        activityHistory: updatedActivityHistory,
        weeklyProgress,
      };

      newlyUnlockedBadges = await calculateBadges(user.uid, progressForBadgeCalc, answer);

      const updatePayload: any = {
        totalAnswered: simulatedTotalAnswered,
        totalCorrect: simulatedTotalCorrect,
        history: arrayUnion(answer),
        freeQuestionsUsed: freeQuestionsUsed + 1,
        lastActivityDate: today,
        currentStreak: newStreak,
        longestStreak,
        activityHistory: updatedActivityHistory,
        weeklyProgress,
      };

      if (newlyUnlockedBadges.length > 0) {
        updatePayload.badges = arrayUnion(...newlyUnlockedBadges);
      }

      if (!userDoc.exists()) {
        tx.set(userRef, {
          ...currentProgress,
          ...updatePayload,
          createdAt: Timestamp.now(),
          badges: newlyUnlockedBadges,
          freeQuestionsUsed: 1,
          currentStreak: 1,
          longestStreak: 1,
          activityHistory: { [today]: 1 },
          lastActivityDate: today,
          weeklyProgress: getRollingWeeklyProgress({ [today]: 1 }, now),
        } as UserProgress);
      } else {
        tx.update(userRef, updatePayload);
      }
    });

    const incrementValue = isCorrect ? 2 : 1;
    if (isProUser) {
      try {
        await updateLeaderboards(user.uid, nickname, photoURL, incrementValue);
      } catch (lbError) {
        console.error('Erro ao atualizar ranking:', lbError);
      }
    }

    return newlyUnlockedBadges.map(b => b.id);
  } catch (error) {
    console.error("Erro ao salvar progresso do usuÃ¡rio:", error);
    return [];
  }
};

export const getGamificationData = async () => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const data = userDoc.data();
      const activityHistory = data.activityHistory || {};
      return {
        currentStreak: data.currentStreak || 0,
        longestStreak: data.longestStreak || 0,
        activityHistory,
        badges: data.badges || [],
        totalAnswered: data.totalAnswered || 0,
        totalCorrect: data.totalCorrect || 0,
        history: data.history || [],
        weeklyGoal: data.weeklyGoal || 50,
        weeklyProgress: getRollingWeeklyProgress(activityHistory),
        purchasedItems: data.purchasedItems || [],
        purchases: data.purchases || {}
      };
    }
    return null;
  } catch (error) {
    console.error("Erro ao buscar dados de gamificaÃ§Ã£o:", error);
    return null;
  }
};

export const getUserProgress = async () => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      return userDoc.data() as UserProgress; // Cast para o tipo UserProgress
    }
    return null;
  } catch (error) {
    console.error("Erro ao buscar progresso do usuÃ¡rio:", error);
    return null;
  }
};

export const getUserSubscription = async (): Promise<UserSubscription | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    const userDoc = await getDoc(userRef);
    const today = toDateKey(new Date());
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      
      // Reset diÃ¡rio se for um novo dia
      let freeQuestionsUsed = data.freeQuestionsUsed || 0;
      if (data.lastActivityDate && data.lastActivityDate !== today) {
        freeQuestionsUsed = 0;
        await updateDoc(userRef, { freeQuestionsUsed: 0 });
      }

      const rawIsPro = data.isPro === true;
      const expiresAtTs = data.expiresAt;
      const expiresMs = expiresAtTs?.toMillis ? expiresAtTs.toMillis() : null;
      const isActivePro = rawIsPro && expiresMs !== null && expiresMs > Date.now();

      // Hardening: evita PRO vitalício sem expiração válida
      if (rawIsPro && !isActivePro) {
        await updateDoc(userRef, {
          isPro: false,
          updatedAt: Timestamp.now(),
        });
      }

      return {
        isPro: isActivePro,
        freeQuestionsUsed: freeQuestionsUsed,
        freeQuestionsLimit: data.freeQuestionsLimit || FREE_QUESTIONS_LIMIT,
        subscribedAt: data.subscribedAt,
        expiresAt: data.expiresAt
      };
    }
    // Criar documento inicial
    await setDoc(userRef, {
      totalAnswered: 0,
      totalCorrect: 0,
      history: [],
      isPro: false,
      freeQuestionsUsed: 0,
      freeQuestionsLimit: FREE_QUESTIONS_LIMIT,
      createdAt: Timestamp.now(),
      lastActivityDate: '',
      currentStreak: 0,
      longestStreak: 0,
      activityHistory: {},
      badges: [],
      weeklyGoal: 50,
      weeklyProgress: 0,
      purchasedItems: [],
      purchases: {}
    });
    return {
      isPro: false,
      freeQuestionsUsed: 0,
      freeQuestionsLimit: FREE_QUESTIONS_LIMIT
    };
  } catch (error) {
    console.error("Erro ao buscar status de assinatura:", error);
    return null;
  }
};

export const canAnswerQuestion = async (): Promise<{ canAnswer: boolean; remaining: number; isPro: boolean }> => {
  const subscription = await getUserSubscription();
  
  if (!subscription) {
    return { canAnswer: false, remaining: 0, isPro: false };
  }
  
  if (subscription.isPro) {
    return { canAnswer: true, remaining: -1, isPro: true };
  }
  
  const remaining = subscription.freeQuestionsLimit - subscription.freeQuestionsUsed;
  return {
    canAnswer: remaining > 0,
    remaining: Math.max(0, remaining),
    isPro: false
  };
};

export const activateProSubscription = async () => {
  const user = auth.currentUser;
  if (!user) return false;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    const now = Timestamp.now();
    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + 1);
    
    await updateDoc(userRef, {
      isPro: true,
      subscribedAt: now,
      expiresAt: Timestamp.fromDate(expiresAt)
    });
    return true;
  } catch (error) {
    console.error("Erro ao ativar assinatura PRO:", error);
    return false;
  }
};

export const getFreeQuestionsRemaining = async (): Promise<number> => {
  const result = await canAnswerQuestion();
  return result.remaining;
};

export async function updateUserSubscription(userId: string, isPro: boolean) {
  const userRef = doc(db, 'userProgress', userId);
  
  await setDoc(userRef, {
    isPro: isPro,
    subscriptionDate: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }, { merge: true });
}

// FunÃ§Ã£o para resetar progresso semanal (chamar toda segunda-feira)
export const resetWeeklyProgress = async () => {
  const user = auth.currentUser;
  if (!user) return;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    await updateDoc(userRef, {
      weeklyProgress: 0
    });
  } catch (error) {
    console.error("Erro ao resetar progresso semanal:", error);
  }
};

// FunÃ§Ã£o para atualizar meta semanal
export const updateWeeklyGoal = async (goal: number) => {
  const user = auth.currentUser;
  if (!user) return;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    await updateDoc(userRef, {
      weeklyGoal: goal
    });
  } catch (error) {
    console.error("Erro ao atualizar meta semanal:", error);
  }
};

// FunÃ§Ã£o para atualizar o perfil do usuÃ¡rio (nickname, targetExam, photoURL)
export const updateUserProfile = async (nickname: string, targetExam: string, photoURL: string) => {
  const user = auth.currentUser;
  if (!user) return;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    await updateDoc(userRef, {
      nickname: nickname,
      targetExam: targetExam,
      photoURL: photoURL, // Assumindo que photoURL tambÃ©m serÃ¡ parte do perfil
    });
  } catch (error) {
    console.error("Erro ao atualizar perfil do usuÃ¡rio:", error);
  }
};

// FunÃ§Ã£o para adicionar/remover bookmark
export const toggleBookmark = async (questionId: string) => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = doc(db, 'userProgress', user.uid);
  try {
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const data = userDoc.data() as UserProgress;
      const bookmarks = data.bookmarks || [];
      
      if (bookmarks.includes(questionId)) {
        // Remover bookmark
        await updateDoc(userRef, {
          bookmarks: bookmarks.filter(id => id !== questionId)
        });
        return false;
      } else {
        // Adicionar bookmark
        await updateDoc(userRef, {
          bookmarks: arrayUnion(questionId)
        });
        return true;
      }
    } else {
      // Se nÃ£o houver documento, criar um com o bookmark
      await setDoc(userRef, {
        totalAnswered: 0,
        totalCorrect: 0,
        history: [],
        isPro: false,
        freeQuestionsUsed: 0,
        freeQuestionsLimit: FREE_QUESTIONS_LIMIT,
        createdAt: Timestamp.now(),
        lastActivityDate: '',
        currentStreak: 0,
        longestStreak: 0,
        activityHistory: {},
        badges: [],
        weeklyGoal: 50,
        weeklyProgress: 0,
        bookmarks: [questionId],
      });
      return true;
    }
  } catch (error) {
    console.error("Erro ao alternar bookmark:", error);
    return null;
  }
};

// Exportar definiÃ§Ãµes de badges para uso na UI
export const getBadgeDefinitions = () => BADGE_DEFINITIONS;



